import { UNPHASED, formatDayLabel, phaseLabel, phaseColor } from '../../utils/metricsFormat';

// Ordem de empilhamento do CFD, da base (fase mais "pronta") ao topo (mais
// "atrasada") — a leitura tradicional de um fluxo cumulativo, onde a faixa
// entre duas camadas é o WIP daquela fase.
const STACK_ORDER = ['completed', 'canceled', 'waiting', 'started', 'unstarted', UNPHASED];

const WIDTH = 760;
const HEIGHT = 220;
const PAD_LEFT = 30;
const PAD_RIGHT = 10;
const PAD_TOP = 10;
const PAD_BOTTOM = 20;

/**
 * Fluxo cumulativo por dia — SVG puro (sem lib de gráfico, ver CLAUDE.md).
 * `days` é a lista completa de dias da janela (cfd_days); `points` é esparso
 * (cfd) — dia sem ninguém em uma fase não gera linha, por isso a matriz
 * dia->fase é preenchida com 0 aqui, não no backend (ver o comentário em
 * BOARD_METRICS_SQL sobre por que cfd_days vem separado).
 */
export default function CumulativeFlowChart({ days, points }) {
  if (!days || !days.length) {
    return <div className="text-xs text-text-secondary py-8 text-center">Sem dados no período.</div>;
  }

  const byDay = new Map(days.map((d) => [d, {}]));
  for (const p of points || []) {
    const row = byDay.get(p.day);
    if (row) row[p.phase ?? UNPHASED] = p.cards;
  }

  const activePhases = STACK_ORDER.filter((ph) => (points || []).some((p) => (p.phase ?? UNPHASED) === ph));
  if (!activePhases.length) {
    return <div className="text-xs text-text-secondary py-8 text-center">Sem cartões no período.</div>;
  }

  const totals = days.map((d) => {
    const row = byDay.get(d) || {};
    return activePhases.reduce((sum, ph) => sum + (row[ph] || 0), 0);
  });
  const maxTotal = Math.max(1, ...totals);

  const plotW = WIDTH - PAD_LEFT - PAD_RIGHT;
  const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const xAt = (i) => PAD_LEFT + (days.length === 1 ? plotW / 2 : (i / (days.length - 1)) * plotW);
  const yAt = (v) => PAD_TOP + plotH - (v / maxTotal) * plotH;

  // Acumulado por dia na ordem de empilhamento: cumBefore[i] é a base da
  // camada atual, cumAfter[i] o topo (que vira a base da próxima).
  let cumBefore = days.map(() => 0);
  const layers = activePhases.map((phase) => {
    const cumAfter = days.map((d, i) => cumBefore[i] + (byDay.get(d)?.[phase] || 0));
    const top = days.map((d, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(cumAfter[i]).toFixed(1)}`);
    const bottom = days.map((d, i) => `L${xAt(i).toFixed(1)},${yAt(cumBefore[i]).toFixed(1)}`).reverse();
    const path = `${top.join(' ')} ${bottom.join(' ')} Z`;
    const layer = { phase, path };
    cumBefore = cumAfter;
    return layer;
  });

  const tickCount = Math.min(days.length, 6);
  const tickIdx = [...new Set(
    Array.from({ length: tickCount }, (_, i) => Math.round((i / Math.max(1, tickCount - 1)) * (days.length - 1)))
  )];

  return (
    <div>
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        {[0, 0.5, 1].map((f) => (
          <line
            key={f}
            x1={PAD_LEFT} x2={WIDTH - PAD_RIGHT}
            y1={PAD_TOP + plotH * (1 - f)} y2={PAD_TOP + plotH * (1 - f)}
            className="stroke-border" strokeWidth="1"
          />
        ))}
        <text x={PAD_LEFT - 5} y={PAD_TOP + plotH + 3} textAnchor="end" className="fill-text-secondary" fontSize="9">0</text>
        <text x={PAD_LEFT - 5} y={PAD_TOP + 8} textAnchor="end" className="fill-text-secondary" fontSize="9">{maxTotal}</text>

        {layers.map((layer) => (
          <path key={layer.phase} d={layer.path} className={phaseColor(layer.phase).fill} opacity="0.85" />
        ))}

        {tickIdx.map((i) => (
          <text key={i} x={xAt(i)} y={HEIGHT - 4} textAnchor="middle" className="fill-text-secondary" fontSize="9">
            {formatDayLabel(days[i])}
          </text>
        ))}
      </svg>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 justify-center">
        {activePhases.map((phase) => (
          <div key={phase} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-sm ${phaseColor(phase).bg}`} />
            <span className="text-[11px] text-text-secondary">{phaseLabel(phase)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
